import { Box, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { ChevronDown, CircleHelp, Filter, MoreVertical } from 'lucide-react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { EllipsesMenu } from 'src/components/EllipsesMenu/ellipses-menu';
import { getNavFloatShadow } from 'src/components/Navbar/DesktopDrawer/desktop-drawer.styles';
import { OptionCheckbox, SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import {
  chevronSx,
  FilterChip,
  FilterChipCount,
  filterChipIconSx,
  HelpButton,
  OverflowButton,
  ScopeButton,
  ScopeHeading,
  ScopeLabel,
  Toolbar,
} from './top-controls.styles';

export type PolicyFilterMode = 'applicable' | 'all';

const filterModeLabels: Record<PolicyFilterMode, string> = {
  applicable: 'Applicable',
  all: 'All',
};

const CATEGORY_ALL = 'All';

// escalation policies are provisioned by useEnsureEscalationPolicies, never by hand
const isEscalationOnlyFilter = (selectedCategories: string[]) =>
  selectedCategories.length === 1 && selectedCategories[0] === POLICY_CATEGORY_ESCALATION;

// denser than the portal default; scoped here so other dropdowns keep their metrics
const policyMenuSx: SystemStyleObject<Theme> = {
  borderRadius: '0.625rem',
  minWidth: '11.875rem',
  backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
  boxShadow: (theme) => getNavFloatShadow(theme),
  // doubled selector: CommandRoot's rules are injected later and would win the tie
  '&& [cmdk-list]': { padding: '0.3125rem' },
  '&& [cmdk-item]': {
    gap: '0.5rem',
    padding: '0.375rem 0.5rem',
    borderRadius: '0.375rem',
  },
};

// SelectMenu values are strings, but the scope id is number | null
const GLOBAL_SCOPE_VALUE = '__global__';

export interface SupportPolicyTopControlsProps {
  scopeLabel: string;
  scopeOptions: { id: number | null; label: string }[];
  scopeValue: number | null;
  onScopeChange: (id: number | null) => void;
  canChangeScope: boolean;
  policyFilterMode: PolicyFilterMode;
  onPolicyFilterModeChange: (mode: PolicyFilterMode) => void;
  totalPoliciesCount: number;
  applicablePoliciesCount: number;
  displayedPoliciesCount: number;
  categories: readonly string[];
  selectedCategories: string[];
  onCategoryToggle: (category: string) => void;
  categoryCounts: Record<string, number>;
  showArchived: boolean;
  setShowArchived: Dispatch<SetStateAction<boolean>>;
  archivedCount: number;
  hasAccessToManageSupportPolicies: boolean;
  onAddPolicy: () => void;
  showLegend: boolean;
  onToggleLegend: () => void;
}

type ScopeControlProps = Pick<
  SupportPolicyTopControlsProps,
  'scopeLabel' | 'scopeOptions' | 'scopeValue' | 'onScopeChange' | 'canChangeScope'
>;

const ScopeControl = ({
  scopeLabel,
  scopeOptions,
  scopeValue,
  onScopeChange,
  canChangeScope,
}: ScopeControlProps) => {
  const options: SelectMenuOption[] = useMemo(() => {
    const globalEntry = scopeOptions.find((option) => option.id === null);
    const partnerEntries = scopeOptions.filter((option) => option.id !== null);
    const built: SelectMenuOption[] = [];
    if (globalEntry) {
      built.push({ value: GLOBAL_SCOPE_VALUE, title: globalEntry.label, group: 'Global' });
    }
    partnerEntries.forEach((option) => {
      built.push({ value: String(option.id), title: option.label, group: 'Partners' });
    });
    return built;
  }, [scopeOptions]);

  if (!canChangeScope) {
    return <ScopeHeading>{scopeLabel}</ScopeHeading>;
  }

  return (
    <SelectMenu
      label="Policy scope"
      searchPlaceholder="Search partners"
      options={options}
      sectioned
      value={scopeValue === null ? GLOBAL_SCOPE_VALUE : String(scopeValue)}
      onChange={(value) => onScopeChange(value === GLOBAL_SCOPE_VALUE ? null : Number(value))}
      menuWidth="16rem"
      menuSx={policyMenuSx}
      renderTrigger={({ open, getTriggerProps }) => (
        <ScopeButton {...getTriggerProps()} type="button">
          <ScopeLabel>{scopeLabel}</ScopeLabel>
          <Box aria-hidden sx={chevronSx(open)}>
            <ChevronDown size={13} />
          </Box>
        </ScopeButton>
      )}
    />
  );
};

type FilterControlProps = Pick<
  SupportPolicyTopControlsProps,
  | 'policyFilterMode'
  | 'onPolicyFilterModeChange'
  | 'totalPoliciesCount'
  | 'applicablePoliciesCount'
  | 'displayedPoliciesCount'
  | 'categories'
  | 'selectedCategories'
  | 'onCategoryToggle'
  | 'categoryCounts'
  | 'showArchived'
  | 'setShowArchived'
  | 'archivedCount'
>;

const FilterControl = ({
  policyFilterMode,
  onPolicyFilterModeChange,
  totalPoliciesCount,
  applicablePoliciesCount,
  displayedPoliciesCount,
  categories,
  selectedCategories,
  onCategoryToggle,
  categoryCounts,
  showArchived,
  setShowArchived,
  archivedCount,
}: FilterControlProps) => {
  const [open, setOpen] = useState(false);

  const selectedRealCategories = useMemo(
    () => selectedCategories.filter((category) => category !== CATEGORY_ALL),
    [selectedCategories],
  );

  const isLit =
    policyFilterMode !== 'applicable' || selectedRealCategories.length > 0 || showArchived;

  const title = useMemo(() => {
    const axisLabels = [
      ...(policyFilterMode !== 'applicable' ? [filterModeLabels.all] : []),
      ...selectedRealCategories,
    ];
    if (axisLabels.length === 0 && !showArchived) return 'Display options';
    const parts = [axisLabels.join(', '), showArchived ? 'archived shown' : ''].filter(
      Boolean,
    );
    return `Display options — ${parts.join(' · ')}`;
  }, [policyFilterMode, selectedRealCategories, showArchived]);

  const options: SelectMenuOption[] = useMemo(() => {
    const showOptions: SelectMenuOption[] = [
      {
        value: 'mode-applicable',
        title: filterModeLabels.applicable,
        hint: String(applicablePoliciesCount),
        group: 'Show',
        adornment: <OptionCheckbox checked={policyFilterMode === 'applicable'} />,
        onSelect: () => {
          onPolicyFilterModeChange('applicable');
          setOpen(false);
        },
      },
      {
        value: 'mode-all',
        title: filterModeLabels.all,
        hint: String(totalPoliciesCount),
        group: 'Show',
        adornment: <OptionCheckbox checked={policyFilterMode === 'all'} />,
        onSelect: () => {
          onPolicyFilterModeChange('all');
          setOpen(false);
        },
      },
    ];

    const categoryOptions: SelectMenuOption[] = categories
      .filter((category) => category !== CATEGORY_ALL)
      .map((category) => ({
        value: `category-${category}`,
        title: category,
        hint: String(categoryCounts[category] ?? 0),
        group: 'Category',
        adornment: <OptionCheckbox checked={selectedCategories.includes(category)} />,
        onSelect: () => onCategoryToggle(category),
      }));

    const archivedOption: SelectMenuOption = {
      value: 'show-archived',
      title: 'Show archived',
      hint: String(archivedCount),
      adornment: <OptionCheckbox checked={showArchived} />,
      onSelect: () => setShowArchived((prev) => !prev),
    };

    return [...showOptions, ...categoryOptions, archivedOption];
  }, [
    policyFilterMode,
    applicablePoliciesCount,
    totalPoliciesCount,
    categories,
    categoryCounts,
    selectedCategories,
    archivedCount,
    showArchived,
    onPolicyFilterModeChange,
    onCategoryToggle,
    setShowArchived,
  ]);

  return (
    <SelectMenu
      label="Display options"
      options={options}
      sectioned
      open={open}
      onOpenChange={setOpen}
      menuWidth="14rem"
      menuSx={policyMenuSx}
      renderTrigger={({ open: menuOpen, getTriggerProps }) => (
        <FilterChip {...getTriggerProps()} type="button" lit={isLit} title={title}>
          <Box aria-hidden sx={filterChipIconSx}>
            <Filter size={13} />
          </Box>
          <span>{filterModeLabels[policyFilterMode]}</span>
          <FilterChipCount>{displayedPoliciesCount}</FilterChipCount>
          <Box aria-hidden sx={chevronSx(menuOpen)}>
            <ChevronDown size={13} />
          </Box>
        </FilterChip>
      )}
    />
  );
};

const LegendControl = ({
  showLegend,
  onToggleLegend,
}: Pick<SupportPolicyTopControlsProps, 'showLegend' | 'onToggleLegend'>) => (
  <HelpButton
    type="button"
    lit={showLegend}
    aria-label="Policy legend"
    aria-expanded={showLegend}
    aria-controls="support-policy-legend"
    onClick={onToggleLegend}
  >
    <CircleHelp size={15} />
  </HelpButton>
);

// Pure action menu, so it uses EllipsesMenu rather than SelectMenu: cmdk marks its
// first row active on open, which reads as a pre-selected option when there is only one.
const OverflowControl = ({ onAddPolicy }: { onAddPolicy: () => void }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <OverflowButton
        type="button"
        aria-label="More actions"
        data-testid="support-policy-overflow"
        aria-haspopup="true"
        aria-expanded={anchorEl ? 'true' : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreVertical size={16} />
      </OverflowButton>
      <V2ThemeProvider>
        <EllipsesMenu
          hideTrigger
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          options={[
            {
              label: 'Add policy',
              icon: 'plus',
              iconLibrary: 'lucide',
              onClick: onAddPolicy,
              testId: 'support-policy-add',
            },
          ]}
        />
      </V2ThemeProvider>
    </>
  );
};

export const SupportPolicyTopControls = ({
  scopeLabel,
  scopeOptions,
  scopeValue,
  onScopeChange,
  canChangeScope,
  policyFilterMode,
  onPolicyFilterModeChange,
  totalPoliciesCount,
  applicablePoliciesCount,
  displayedPoliciesCount,
  categories,
  selectedCategories,
  onCategoryToggle,
  categoryCounts,
  showArchived,
  setShowArchived,
  archivedCount,
  hasAccessToManageSupportPolicies,
  onAddPolicy,
  showLegend,
  onToggleLegend,
}: SupportPolicyTopControlsProps) => {
  // the scope and filter controls have always been manage-gated, long before this
  // toolbar held a scope picker — view-only users are not losing a control they
  // had. Giving those back is its own change, tracked separately. The legend is
  // not one of them: reading the severity icons is exactly the front line's job.
  if (!hasAccessToManageSupportPolicies) {
    return (
      <Toolbar sx={{ justifyContent: 'flex-end' }}>
        <LegendControl showLegend={showLegend} onToggleLegend={onToggleLegend} />
      </Toolbar>
    );
  }

  return (
    <Toolbar>
      <ScopeControl
        scopeLabel={scopeLabel}
        scopeOptions={scopeOptions}
        scopeValue={scopeValue}
        onScopeChange={onScopeChange}
        canChangeScope={canChangeScope}
      />
      <FilterControl
        policyFilterMode={policyFilterMode}
        onPolicyFilterModeChange={onPolicyFilterModeChange}
        totalPoliciesCount={totalPoliciesCount}
        applicablePoliciesCount={applicablePoliciesCount}
        displayedPoliciesCount={displayedPoliciesCount}
        categories={categories}
        selectedCategories={selectedCategories}
        onCategoryToggle={onCategoryToggle}
        categoryCounts={categoryCounts}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        archivedCount={archivedCount}
      />
      <LegendControl showLegend={showLegend} onToggleLegend={onToggleLegend} />
      {isEscalationOnlyFilter(selectedCategories) ? null : (
        <OverflowControl onAddPolicy={onAddPolicy} />
      )}
    </Toolbar>
  );
};
